package com.iptvplayer.shared.data.repository

import com.iptvplayer.shared.data.local.cache.ContentCache
import com.iptvplayer.shared.data.parser.M3UParser
import com.iptvplayer.shared.data.remote.ProviderHttpException
import com.iptvplayer.shared.data.remote.api.SourceApi
import com.iptvplayer.shared.data.remote.api.toAddRequest
import com.iptvplayer.shared.data.remote.api.toDomain
import com.iptvplayer.shared.data.remote.xtream.XtreamApiClient
import com.iptvplayer.shared.domain.model.Channel
import com.iptvplayer.shared.domain.model.IptvSource
import com.iptvplayer.shared.domain.model.Playlist
import com.iptvplayer.shared.domain.model.SeriesInfo
import com.iptvplayer.shared.domain.model.SeriesItem
import com.iptvplayer.shared.domain.model.SourceFailure
import com.iptvplayer.shared.domain.model.SyncReport
import com.iptvplayer.shared.domain.model.VodItem
import com.iptvplayer.shared.domain.repository.ContentRepository
import com.iptvplayer.shared.util.Result
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.network.sockets.ConnectTimeoutException
import io.ktor.client.network.sockets.SocketTimeoutException
import io.ktor.client.plugins.ClientRequestException
import io.ktor.client.plugins.HttpRequestTimeoutException
import io.ktor.client.plugins.ServerResponseException
import io.ktor.client.request.*
import io.ktor.http.isSuccess
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.coroutines.withContext

/**
 * Content repository with two storage modes:
 *
 * - **DB mode** (Android / iOS / desktop — [ContentCache.isAvailable]): the
 *   catalog lives in SQLite only. Sync streams each source's playlist straight
 *   to disk, and every read (Home rows, Browse pages, search, zapping) is a
 *   windowed/indexed SQL query. The full catalog is never held in RAM.
 *
 * - **RAM mode** (JS/web — no SQLite driver): the legacy in-memory flows are
 *   kept as the source of truth and the browse / search methods fall back to
 *   filtering them, so the web client works unchanged.
 */
class ContentRepositoryImpl(
    private val m3uParser: M3UParser,
    private val xtreamApiClient: XtreamApiClient,
    private val httpClient: HttpClient,
    private val sourceApi: SourceApi,
    private val contentCache: ContentCache
) : ContentRepository {

    private val sourcesFlow = MutableStateFlow<List<IptvSource>>(emptyList())

    // RAM-mode only (JS/web). In DB mode these stay empty — the DB is the
    // single source of truth and holding the catalog here would double memory.
    private val channelsFlow = MutableStateFlow<List<Channel>>(emptyList())
    private val vodItemsFlow = MutableStateFlow<List<VodItem>>(emptyList())
    private val seriesFlow = MutableStateFlow<List<SeriesItem>>(emptyList())
    private val categoriesFlow = MutableStateFlow<List<String>>(emptyList())

    private val dbMode get() = contentCache.isAvailable

    private val syncMutex = Mutex()

    private val _syncStatus = MutableStateFlow<String?>(null)
    override val syncStatus: Flow<String?> get() = _syncStatus
    private var sourcesSynced = false

    private val seriesMutex = Mutex()
    private var seriesSynced = false

    private companion object {
        const val CACHE_TTL_MS = 2L * 24 * 60 * 60 * 1000 // 2 days
        const val SEARCH_LIMIT = 200L
    }

    override suspend fun addSource(source: IptvSource): Result<Unit> = try {
        // Persist on the backend first so it survives reinstalls / syncs across devices.
        sourceApi.addSource(source.toAddRequest())
        val current = sourcesFlow.value.toMutableList()
        current.removeAll { it.id == source.id }
        current.add(source)
        sourcesFlow.value = current
        contentCache.invalidate() // sources changed → refresh cache next launch
        Result.Success(Unit)
    } catch (e: Exception) {
        Result.Error("Failed to add source: ${e.message}", e)
    }

    override suspend fun removeSource(sourceId: String): Result<Unit> = try {
        sourceApi.removeSource(sourceId)
        sourcesFlow.value = sourcesFlow.value.filter { it.id != sourceId }
        if (!dbMode) {
            channelsFlow.value = channelsFlow.value.filter { it.sourceId != sourceId }
            vodItemsFlow.value = vodItemsFlow.value.filter { it.sourceId != sourceId }
            seriesFlow.value = seriesFlow.value.filter { it.sourceId != sourceId }
            rebuildCategories()
        }
        contentCache.deleteSourceContent(sourceId) // drop rows so paged Browse updates now
        contentCache.invalidate() // sources changed → refresh cache next launch
        Result.Success(Unit)
    } catch (e: Exception) {
        Result.Error("Failed to remove source: ${e.message}", e)
    }

    override suspend fun syncSources(force: Boolean): Result<SyncReport> = syncMutex.withLock {
        if (sourcesSynced && !force) {
            return@withLock Result.Success(SyncReport(sourcesFlow.value.size, fromCache = true))
        }

        // Use the local cache when it's fresh (< 2 days) and we're not forcing a
        // refresh — avoids hitting the backend/panels on every launch.
        if (!force && contentCache.isFresh(CACHE_TTL_MS)) {
            val cachedSources = contentCache.loadSources()
            if (cachedSources.isNotEmpty()) {
                sourcesFlow.value = cachedSources
                if (dbMode) {
                    // DB mode: content is read straight from SQLite windows —
                    // nothing to load into RAM. Just note whether series rows
                    // already exist so we can skip the multi-MB lazy re-fetch.
                    seriesSynced = contentCache.hasSeries()
                } else {
                    channelsFlow.value = contentCache.loadChannels()
                    vodItemsFlow.value = contentCache.loadVod()
                    val cachedSeries = contentCache.loadSeries()
                    if (cachedSeries.isNotEmpty()) {
                        seriesFlow.value = cachedSeries
                        seriesSynced = true
                    }
                    rebuildCategories()
                }
                sourcesSynced = true
                return@withLock Result.Success(SyncReport(cachedSources.size, fromCache = true))
            }
        }

        try {
            if (force) {
                channelsFlow.value = emptyList()
                vodItemsFlow.value = emptyList()
                seriesFlow.value = emptyList()
                categoriesFlow.value = emptyList()
                seriesSynced = false
            }
            val previousIds = if (dbMode) contentCache.loadSources().map { it.id } else emptyList()
            val remote = sourceApi.getSources().mapNotNull { it.toDomain() }
            sourcesFlow.value = remote
            // loadPlaylist replaces a source's content, so re-running is idempotent.
            // In DB mode each playlist is persisted per source inside loadPlaylist,
            // so the full multi-source catalog is never accumulated in RAM.
            // Per-source failures are collected — not swallowed — so the UI can
            // tell the user their provider is the problem.
            val failures = mutableListOf<SourceFailure>()
            remote.forEachIndexed { index, source ->
                _syncStatus.value = if (remote.size > 1) {
                    "Syncing ${source.name} (${index + 1}/${remote.size})…"
                } else {
                    "Syncing ${source.name}…"
                }
                val result = runCatching { loadPlaylist(source) }
                    .getOrElse { Result.Error(it.message ?: "Unexpected error", it) }
                if (result is Result.Error) {
                    failures += SourceFailure(source.name, classifyFailure(result.throwable), result.message)
                }
            }
            _syncStatus.value = null
            val report = SyncReport(sourceCount = remote.size, failures = failures)
            if (dbMode) {
                // Drop content of sources that no longer exist on the backend.
                val remoteIds = remote.map { it.id }.toSet()
                previousIds.filter { it !in remoteIds }
                    .forEach { contentCache.deleteSourceContent(it) }
                contentCache.saveSources(remote)
                // A fully-failed sync must not stamp the cache as fresh — the
                // next launch should retry instead of trusting empty tables.
                if (!report.allFailed) contentCache.markSynced()
            } else {
                // RAM mode (JS): no SQLite — save() is a no-op there but kept
                // for symmetry with any driver-backed platform in RAM mode.
                contentCache.save(remote, channelsFlow.value, vodItemsFlow.value)
            }
            // Same idea in-session: leave sourcesSynced=false when everything
            // failed so the next screen visit retries automatically.
            sourcesSynced = !report.allFailed
            Result.Success(report)
        } catch (e: Exception) {
            Result.Error("Failed to sync sources: ${e.message}", e)
        } finally {
            _syncStatus.value = null
        }
    }

    /** Map a playlist-load failure to a user-explainable kind. */
    private fun classifyFailure(root: Throwable?): SourceFailure.Kind {
        var cause = root
        var hops = 0
        while (cause != null && hops < 8) {
            when (cause) {
                is ProviderHttpException -> return when {
                    cause.status == 401 || cause.status == 403 -> SourceFailure.Kind.AUTH_FAILED
                    else -> SourceFailure.Kind.PROVIDER_DOWN
                }
                is ServerResponseException -> return SourceFailure.Kind.PROVIDER_DOWN
                is ClientRequestException -> return when (cause.response.status.value) {
                    401, 403 -> SourceFailure.Kind.AUTH_FAILED
                    else -> SourceFailure.Kind.PROVIDER_DOWN
                }
                is HttpRequestTimeoutException,
                is ConnectTimeoutException,
                is SocketTimeoutException -> return SourceFailure.Kind.NETWORK
                else -> {
                    val m = cause.message?.lowercase() ?: ""
                    if (listOf("connect", "resolve", "unreachable", "network", "timeout", "refused")
                            .any { it in m }
                    ) return SourceFailure.Kind.NETWORK
                }
            }
            cause = cause.cause?.takeIf { it !== cause }
            hops++
        }
        return SourceFailure.Kind.UNKNOWN
    }

    override fun getSources(): Flow<List<IptvSource>> = sourcesFlow

    override suspend fun loadPlaylist(source: IptvSource): Result<Playlist> = try {
        val playlist = when (source) {
            is IptvSource.M3USource -> loadM3UPlaylist(source)
            is IptvSource.XtreamSource -> loadXtreamPlaylist(source)
        }

        if (dbMode) {
            // Stream straight to disk; reactive DB windows pick the rows up.
            contentCache.saveSourceContent(source.id, playlist.channels, playlist.vodItems)
        } else {
            // Merge into the in-RAM catalog (web).
            val currentChannels = channelsFlow.value.toMutableList()
            currentChannels.removeAll { it.sourceId == source.id }
            currentChannels.addAll(playlist.channels)
            channelsFlow.value = currentChannels

            val currentVod = vodItemsFlow.value.toMutableList()
            currentVod.removeAll { it.sourceId == source.id }
            currentVod.addAll(playlist.vodItems)
            vodItemsFlow.value = currentVod

            val currentSeries = seriesFlow.value.toMutableList()
            currentSeries.removeAll { it.sourceId == source.id }
            currentSeries.addAll(playlist.series)
            seriesFlow.value = currentSeries

            rebuildCategories()
        }

        Result.Success(playlist)
    } catch (e: Exception) {
        Result.Error("Failed to load playlist: ${e.message}", e)
    }

    override fun getChannels(sourceId: String?): Flow<List<Channel>> =
        channelsFlow.map { channels ->
            if (sourceId != null) channels.filter { it.sourceId == sourceId } else channels
        }

    override fun getVodItems(sourceId: String?): Flow<List<VodItem>> =
        vodItemsFlow.map { items ->
            if (sourceId != null) items.filter { it.sourceId == sourceId } else items
        }

    override fun getCategories(sourceId: String?): Flow<List<String>> =
        if (sourceId != null) {
            channelsFlow.map { channels ->
                channels.filter { it.sourceId == sourceId }
                    .mapNotNull { it.group }
                    .distinct()
                    .sorted()
            }
        } else {
            categoriesFlow
        }

    override fun getChannelsByCategory(category: String): Flow<List<Channel>> =
        channelsFlow.map { channels ->
            channels.filter { it.group == category }
        }

    override fun getVodByCategory(category: String): Flow<List<VodItem>> =
        vodItemsFlow.map { items ->
            items.filter { it.group == category }
        }

    override fun getSeries(sourceId: String?): Flow<List<SeriesItem>> =
        seriesFlow.map { items ->
            if (sourceId != null) items.filter { it.sourceId == sourceId } else items
        }

    override fun getSeriesByCategory(category: String): Flow<List<SeriesItem>> =
        seriesFlow.map { items -> items.filter { it.group == category } }

    /** Replace [srcId]'s series in the shared RAM flow with [items] (RAM mode only). */
    private fun publishSeries(srcId: String, items: Collection<SeriesItem>) {
        val merged = seriesFlow.value.toMutableList()
        merged.removeAll { it.sourceId == srcId }
        merged.addAll(items)
        seriesFlow.value = merged
    }

    override suspend fun refreshSeries(): Result<Unit> {
        var failed = 0
        sourcesFlow.value.filterIsInstance<IptvSource.XtreamSource>().forEach { src ->
            runCatching {
                val categories = runCatching {
                    xtreamApiClient.getSeriesCategories(src.serverUrl, src.username, src.password)
                }.getOrDefault(emptyList())

                // Fetch series PER CATEGORY rather than in one call. The full
                // get_series payload is ~10 MB on large panels, which stalls /
                // times out the JS (web) client; per-category responses are tiny,
                // resilient (one bad category doesn't lose them all), and let the
                // list populate progressively.
                if (categories.isNotEmpty()) {
                    val accumulated = LinkedHashMap<Int, SeriesItem>()
                    if (!dbMode) publishSeries(src.id, emptyList()) // clear this source first
                    categories.forEachIndexed { catIndex, cat ->
                        _syncStatus.value = "Loading series ${catIndex + 1}/${categories.size} categories…"
                        val items = runCatching {
                            xtreamApiClient.mapSeriesListToItems(
                                xtreamApiClient.getSeries(src.serverUrl, src.username, src.password, cat.categoryId),
                                src.id
                            )
                        }.getOrNull() ?: return@forEach
                        items.forEach { s ->
                            accumulated[s.seriesId] = s.copy(group = cat.categoryName.ifBlank { s.group })
                        }
                        // Progressive update: DB windows (native) or RAM flow (web)
                        // re-emit as each category lands.
                        if (dbMode) contentCache.saveSeriesForSource(src.id, accumulated.values)
                        else publishSeries(src.id, accumulated.values)
                    }
                } else {
                    // No category list — fall back to a single fetch.
                    val items = xtreamApiClient.mapSeriesListToItems(
                        xtreamApiClient.getSeries(src.serverUrl, src.username, src.password),
                        src.id
                    )
                    if (dbMode) contentCache.saveSeriesForSource(src.id, items)
                    else publishSeries(src.id, items)
                }
            }.onFailure { failed++ }
        }
        if (!dbMode) {
            // RAM mode: persist the snapshot (no-op on JS) and refresh categories.
            contentCache.saveSeries(seriesFlow.value)
            rebuildCategories()
        }
        _syncStatus.value = null
        if (failed == 0) seriesSynced = true
        return if (failed == 0) Result.Success(Unit) else Result.Error("Failed to refresh $failed source(s)")
    }

    override suspend fun ensureSeriesLoaded(): Result<Unit> = seriesMutex.withLock {
        if (seriesSynced) Result.Success(Unit) else refreshSeries()
    }

    override suspend fun loadSeriesInfo(sourceId: String, seriesId: Int): Result<SeriesInfo> {
        val source = sourcesFlow.value.find { it.id == sourceId }
            ?: return Result.Error("Source not found: $sourceId")
        if (source !is IptvSource.XtreamSource) {
            return Result.Error("Series detail is only available for Xtream sources")
        }
        val item = seriesFlow.value.firstOrNull { it.sourceId == sourceId && it.seriesId == seriesId }
            ?: contentCache.findSeries(sourceId, seriesId.toLong())
            ?: return Result.Error("Series not found")
        return try {
            val response = xtreamApiClient.getSeriesInfo(source.serverUrl, source.username, source.password, seriesId)
            val info = xtreamApiClient.mapSeriesInfo(item, response, source.serverUrl, source.username, source.password)
            Result.Success(info)
        } catch (e: Exception) {
            Result.Error("Failed to load series info: ${e.message}", e)
        }
    }

    override suspend fun nextChannel(currentId: String, direction: Int): Channel? =
        if (dbMode) {
            contentCache.zapChannel(currentId, direction)
        } else {
            val all = channelsFlow.value
            if (all.isEmpty()) null else {
                val current = all.firstOrNull { it.id == currentId }
                val pool = current?.group
                    ?.let { g -> all.filter { it.group == g } }
                    ?.takeIf { it.size > 1 } ?: all
                val idx = pool.indexOfFirst { it.id == currentId }
                pool[((if (idx < 0) 0 else idx) + direction + pool.size) % pool.size]
            }
        }

    override suspend fun refreshContent(sourceId: String): Result<Unit> {
        val source = sourcesFlow.value.find { it.id == sourceId }
            ?: return Result.Error("Source not found: $sourceId")
        val result = loadPlaylist(source).map { }
        // DB mode persists per-source inside loadPlaylist; RAM mode re-saves the
        // snapshot (no-op on JS). Never call save() with the empty RAM flows in
        // DB mode — that would wipe the tables.
        if (result is Result.Success && !dbMode) {
            contentCache.save(sourcesFlow.value, channelsFlow.value, vodItemsFlow.value)
        }
        return result
    }

    // ──────────────────── Paged Browse (windowed) ────────────────────
    // DB mode: reactive SQL windows (nothing in RAM). RAM mode (web): the same
    // API served by filtering the in-memory flows, so shared viewmodels work on
    // every platform. blank query/category is normalized to null (= disabled).

    override fun browseChannels(category: String?, query: String?, limit: Long): Flow<List<Channel>> {
        val cat = category?.ifBlank { null }
        val q = query?.ifBlank { null }
        if (dbMode) return contentCache.channelsWindow(cat, q, limit)
        val lower = q?.lowercase()
        return channelsFlow.map { list ->
            list.asSequence()
                .filter { cat == null || it.group == cat }
                .filter { lower == null || it.name.lowercase().contains(lower) }
                .take(limit.toInt())
                .toList()
        }
    }

    override fun browseChannelCount(category: String?, query: String?): Flow<Long> {
        val cat = category?.ifBlank { null }
        val q = query?.ifBlank { null }
        if (dbMode) return contentCache.channelsCount(cat, q)
        val lower = q?.lowercase()
        return channelsFlow.map { list ->
            list.count {
                (cat == null || it.group == cat) &&
                        (lower == null || it.name.lowercase().contains(lower))
            }.toLong()
        }
    }

    override fun channelGroups(): Flow<List<String>> =
        if (dbMode) contentCache.channelGroups()
        else channelsFlow.map { list -> list.mapNotNull { it.group }.distinct().sorted() }

    override fun browseVod(category: String?, query: String?, limit: Long): Flow<List<VodItem>> {
        val cat = category?.ifBlank { null }
        val q = query?.ifBlank { null }
        if (dbMode) return contentCache.vodWindow(cat, q, limit)
        val lower = q?.lowercase()
        return vodItemsFlow.map { list ->
            list.asSequence()
                .filter { cat == null || it.group == cat }
                .filter { lower == null || it.name.lowercase().contains(lower) }
                .take(limit.toInt())
                .toList()
        }
    }

    override fun browseVodCount(category: String?, query: String?): Flow<Long> {
        val cat = category?.ifBlank { null }
        val q = query?.ifBlank { null }
        if (dbMode) return contentCache.vodCount(cat, q)
        val lower = q?.lowercase()
        return vodItemsFlow.map { list ->
            list.count {
                (cat == null || it.group == cat) &&
                        (lower == null || it.name.lowercase().contains(lower))
            }.toLong()
        }
    }

    override fun vodGroups(): Flow<List<String>> =
        if (dbMode) contentCache.vodGroups()
        else vodItemsFlow.map { list -> list.mapNotNull { it.group }.distinct().sorted() }

    override fun browseSeries(category: String?, query: String?, limit: Long): Flow<List<SeriesItem>> {
        val cat = category?.ifBlank { null }
        val q = query?.ifBlank { null }
        if (dbMode) return contentCache.seriesWindow(cat, q, limit)
        val lower = q?.lowercase()
        return seriesFlow.map { list ->
            list.asSequence()
                .filter { cat == null || it.group == cat }
                .filter { lower == null || it.name.lowercase().contains(lower) }
                .take(limit.toInt())
                .toList()
        }
    }

    override fun browseSeriesCount(category: String?, query: String?): Flow<Long> {
        val cat = category?.ifBlank { null }
        val q = query?.ifBlank { null }
        if (dbMode) return contentCache.seriesCount(cat, q)
        val lower = q?.lowercase()
        return seriesFlow.map { list ->
            list.count {
                (cat == null || it.group == cat) &&
                        (lower == null || it.name.lowercase().contains(lower))
            }.toLong()
        }
    }

    override fun seriesGroups(): Flow<List<String>> =
        if (dbMode) contentCache.seriesGroups()
        else seriesFlow.map { list -> list.mapNotNull { it.group }.distinct().sorted() }

    // ──────────────────── One-shot deep pages ────────────────────

    override suspend fun pageChannels(category: String?, query: String?, limit: Long, offset: Long): List<Channel> {
        val cat = category?.ifBlank { null }
        val q = query?.ifBlank { null }
        if (dbMode) return contentCache.channelsPage(cat, q, limit, offset)
        val lower = q?.lowercase()
        return channelsFlow.value.asSequence()
            .filter { cat == null || it.group == cat }
            .filter { lower == null || it.name.lowercase().contains(lower) }
            .drop(offset.toInt()).take(limit.toInt()).toList()
    }

    override suspend fun pageVod(category: String?, query: String?, limit: Long, offset: Long): List<VodItem> {
        val cat = category?.ifBlank { null }
        val q = query?.ifBlank { null }
        if (dbMode) return contentCache.vodPage(cat, q, limit, offset)
        val lower = q?.lowercase()
        return vodItemsFlow.value.asSequence()
            .filter { cat == null || it.group == cat }
            .filter { lower == null || it.name.lowercase().contains(lower) }
            .drop(offset.toInt()).take(limit.toInt()).toList()
    }

    override suspend fun pageSeries(category: String?, query: String?, limit: Long, offset: Long): List<SeriesItem> {
        val cat = category?.ifBlank { null }
        val q = query?.ifBlank { null }
        if (dbMode) return contentCache.seriesPage(cat, q, limit, offset)
        val lower = q?.lowercase()
        return seriesFlow.value.asSequence()
            .filter { cat == null || it.group == cat }
            .filter { lower == null || it.name.lowercase().contains(lower) }
            .drop(offset.toInt()).take(limit.toInt()).toList()
    }

    // ──────────────────── Private Helpers ────────────────────

    private suspend fun loadM3UPlaylist(source: IptvSource.M3USource): Playlist {
        val response = httpClient.get(source.url)
        if (!response.status.isSuccess()) throw ProviderHttpException(response.status.value)
        val content: String = response.body()
        // Big playlists (100k+ lines) — parse off the main thread.
        return withContext(Dispatchers.Default) { m3uParser.parse(content, source) }
    }

    private suspend fun loadXtreamPlaylist(source: IptvSource.XtreamSource): Playlist = coroutineScope {
        // Authenticate to validate credentials
        xtreamApiClient.authenticate(source.serverUrl, source.username, source.password)

        // The four catalog endpoints are independent — fetch them concurrently
        // so a full sync takes ~1 round-trip instead of 4 sequential ones.
        // Stream entries only carry category_id; the human-readable name lives
        // in the *_categories endpoints. Build lookup maps so channels/movies
        // show real category names instead of "1", "10", "11"…
        val liveCategoryNamesDeferred = async {
            runCatching {
                xtreamApiClient.getLiveCategories(source.serverUrl, source.username, source.password)
                    .associate { it.categoryId to it.categoryName }
            }.getOrDefault(emptyMap())
        }
        val vodCategoryNamesDeferred = async {
            runCatching {
                xtreamApiClient.getVodCategories(source.serverUrl, source.username, source.password)
                    .associate { it.categoryId to it.categoryName }
            }.getOrDefault(emptyMap())
        }
        val liveStreamsDeferred = async {
            xtreamApiClient.getLiveStreams(source.serverUrl, source.username, source.password)
        }
        val vodStreamsDeferred = async {
            xtreamApiClient.getVodStreams(source.serverUrl, source.username, source.password)
        }

        val liveCategoryNames = liveCategoryNamesDeferred.await()
        val vodCategoryNames = vodCategoryNamesDeferred.await()

        val channels = xtreamApiClient.mapLiveStreamsToChannels(
            liveStreamsDeferred.await(), source.id, source.serverUrl, source.username, source.password
        ).map { c -> c.copy(group = liveCategoryNames[c.group]?.takeIf { it.isNotBlank() } ?: c.group) }

        val vodItems = xtreamApiClient.mapVodStreamsToVodItems(
            vodStreamsDeferred.await(), source.id, source.serverUrl, source.username, source.password
        ).map { v -> v.copy(group = vodCategoryNames[v.group]?.takeIf { it.isNotBlank() } ?: v.group) }

        // Series are fetched lazily by refreshSeries() — initial sync only
        // covers live + VOD so the user sees Home/Browse quickly.
        val categories = (channels.mapNotNull { it.group } + vodItems.mapNotNull { it.group })
            .distinct()
            .sorted()

        Playlist(
            source = source,
            channels = channels,
            vodItems = vodItems,
            categories = categories
        )
    }

    private fun rebuildCategories() {
        val channelCategories = channelsFlow.value.mapNotNull { it.group }
        val vodCategories = vodItemsFlow.value.mapNotNull { it.group }
        val seriesCategories = seriesFlow.value.mapNotNull { it.group }
        categoriesFlow.value = (channelCategories + vodCategories + seriesCategories).distinct().sorted()
    }
}
